import { cn } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { AlertTriangle, CircleCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { LegIntel } from '../lib/mockData';

const TABS = ['All', 'Urgent', 'Backlog'] as const;

/** Processing messages walked through on open (≥1s each) before cards appear. */
const PROCESSING_STEPS = ['Analysing', 'Thinking', 'Building'] as const;
const PROCESSING_STEP_MS = 1000;

/**
 * "my i" — Movement Intelligence for Leg Planning. Same shell, size, colours and
 * scanning behaviour as the Manifest load-suggestions panel: a sliding dark-
 * headed overlay that walks a 3-step "processing" state on open, then renders
 * the card stack for the selected leg (SLA urgency, fit drivers, brief, capacity).
 * Prototype — content is mock.
 */
export function LegIntelligence({
  open,
  onClose,
  intel,
}: {
  open: boolean;
  onClose: () => void;
  intel: LegIntel;
}) {
  const [tab, setTab] = useState<(typeof TABS)[number]>('All');
  const [scanning, setScanning] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Walk the processing messages each time the panel opens, then settle.
  useEffect(() => {
    if (!open) {
      setScanning(false);
      return;
    }
    setScanning(true);
    setStepIndex(0);
    const timers = PROCESSING_STEPS.slice(1).map((_, i) =>
      setTimeout(() => setStepIndex(i + 1), (i + 1) * PROCESSING_STEP_MS),
    );
    timers.push(setTimeout(() => setScanning(false), PROCESSING_STEPS.length * PROCESSING_STEP_MS));
    return () => timers.forEach(clearTimeout);
  }, [open]);

  const stepLabel = PROCESSING_STEPS[stepIndex];

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        'absolute inset-y-0 right-0 z-40 flex w-[34rem] max-w-[92%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
      )}
    >
      {/* "my i" bar — only the icon closes the panel. */}
      <div className="flex w-full items-center gap-2.5 border-b border-slate-700 bg-slate-800 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close movement intelligence"
          title="Close"
          className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-700"
        >
          <MyIIcon state={scanning ? 'scanning' : 'idle'} className="h-7 w-7" />
        </button>
        <div className="leading-tight">
          <div className="text-xs font-medium text-slate-100">my i</div>
          <div className="text-[10px] text-slate-400">
            {scanning ? `${stepLabel}…` : 'Movement Intelligence'}
          </div>
        </div>
        <span className="ml-auto whitespace-nowrap text-[9px] text-slate-200">{intel.contextLabel}</span>
      </div>

      {/* Tab chips. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-slate-200 px-3 py-2">
        {TABS.map((t) => {
          const active = t === tab;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors',
                active
                  ? 'border-slate-700 bg-slate-700 text-white'
                  : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700',
              )}
            >
              {t}
            </button>
          );
        })}
      </div>

      {/* Body — processing state until cards settle. */}
      <div className="min-h-0 flex-1 overflow-auto">
        {scanning ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            <MyIIcon state="scanning" className="h-16 w-16" />
            <p key={stepLabel} className="animate-pulse text-sm font-medium text-slate-600">
              {stepLabel}…
            </p>
            <p className="text-xs text-slate-400">Evaluating the selected leg.</p>
          </div>
        ) : (
          <div className="space-y-3 p-3 text-xs">
            {intel.slaCards.map((c, i) => (
              <div
                key={i}
                className={cn(
                  'rounded-md border p-2',
                  c.tone === 'risk' ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50',
                )}
              >
                <div
                  className={cn(
                    'flex items-center gap-1.5 text-[11px] font-semibold',
                    c.tone === 'risk' ? 'text-red-700' : 'text-amber-700',
                  )}
                >
                  <AlertTriangle className="h-3.5 w-3.5" /> SLA
                </div>
                <div className="mt-0.5 text-[11px] font-medium text-slate-700">
                  {c.ref} — {c.title}
                </div>
                <p className="mt-0.5 text-[11px] text-slate-500">{c.body}</p>
                <button
                  type="button"
                  className="mt-1.5 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700"
                >
                  {c.action}
                </button>
              </div>
            ))}

            {intel.fitDrivers.length > 0 && (
              <Block title="Fit">
                <div className="space-y-1.5">
                  {intel.fitDrivers.map((f) => (
                    <div
                      key={f.name}
                      className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/50 px-2 py-1.5"
                    >
                      <span className="rounded bg-emerald-600 px-1 text-[9px] font-bold text-white">FIT</span>
                      <div>
                        <div className="font-medium text-slate-800">{f.name}</div>
                        <div className="text-[10px] text-slate-500">{f.detail}</div>
                      </div>
                    </div>
                  ))}
                  {intel.feasibleNote && (
                    <p className="flex items-center gap-1 text-[10px] text-slate-500">
                      <CircleCheck className="h-3 w-3 text-emerald-500" /> {intel.feasibleNote}
                    </p>
                  )}
                </div>
              </Block>
            )}

            <Block title="Brief">
              <p className="whitespace-pre-wrap rounded-md bg-slate-50 p-2 text-[11px] leading-relaxed text-slate-600">
                {intel.brief}
              </p>
            </Block>

            {intel.capacityNote && (
              <Block title="Capacity">
                <p className="text-[11px] text-slate-500">{intel.capacityNote}</p>
              </Block>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{title}</div>
      {children}
    </div>
  );
}
